<template>
  <div data-private class="flex h-full">
    <!-- Brand panel -->
    <div class="max-md:hidden flex w-[45%] bg-neutral-1000 items-center justify-center relative overflow-hidden">
      <div class="relative z-10 flex flex-col items-center gap-6 px-10">
        <img :src="koMark" alt="Kairon Health" class="w-20 h-auto" />
        <div class="text-center">
          <h1 class="text-3xl font-bold text-white tracking-tight">
            Kairon Health
          </h1>
          <p class="mt-2 text-neutral-300 text-sm">
            Value-based care analytics
          </p>
        </div>
      </div>
    </div>

    <!-- Form panel -->
    <div class="flex-1 flex items-center justify-center bg-primary-50 p-6">
      <div class="w-full max-w-[420px]">
        <div class="bg-white rounded-lg shadow-[0_4px_40px_rgba(0,0,0,0.08)] p-8">
          <h2 class="text-xl font-semibold text-gray-800 text-center mb-6">
            {{ step === 'forgotPassword' ? 'Reset Password' : 'Sign in' }}
          </h2>

          <div
            v-if="sessionExpired"
            class="mb-4 p-3 bg-yellow-100 text-yellow-800 border border-yellow-300 rounded text-sm"
          >
            Your session has expired. Please log in again.
          </div>
          <div
            v-if="error"
            class="mb-4 p-3 bg-red-100 text-red-800 border border-red-300 rounded text-sm"
          >
            {{ error }}
          </div>
          <div
            v-if="success"
            class="mb-4 p-3 bg-green-100 text-green-800 border border-green-300 rounded text-sm"
          >
            {{ success }}
          </div>

          <!-- Step: Email -->
          <form v-if="step === 'email'" class="space-y-5" @submit.prevent="submitEmail">
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1"
                >Email</label
              >
              <input
                id="email"
                ref="emailInput"
                v-model="email"
                type="text"
                required
                autocomplete="username"
                placeholder="name@company.com"
                class="p-2.5 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
              />
            </div>
            <button
              type="submit"
              class="w-full btn-primary"
              :disabled="loading"
            >
              <span v-if="loading">
                <i class="fa-solid fa-spinner fa-spin mr-2"></i>Checking...
              </span>
              <span v-else>Continue</span>
            </button>
          </form>

          <!-- Step: Password -->
          <form v-if="step === 'password'" class="space-y-5" @submit.prevent="submitLogin">
            <div>
              <label for="email-display" class="block text-sm font-medium text-gray-700 mb-1"
                >Email</label
              >
              <input
                id="email-display"
                :value="email"
                type="email"
                disabled
                class="p-2.5 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-500"
              />
            </div>
            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 mb-1"
                >Password</label
              >
              <input
                id="password"
                ref="passwordInput"
                v-model="password"
                type="password"
                required
                autocomplete="current-password"
                class="p-2.5 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
              />
            </div>
            <button
              type="submit"
              class="w-full btn-primary"
              :disabled="loading"
            >
              <span v-if="loading">
                <i class="fa-solid fa-spinner fa-spin mr-2"></i>Signing in...
              </span>
              <span v-else>Sign In</span>
            </button>
            <div class="flex justify-between items-center">
              <button
                type="button"
                class="btn-link"
                @click="goBack"
              >
                <i class="fa-solid fa-arrow-left mr-1"></i>Back
              </button>
              <button
                v-if="!isSandbox"
                type="button"
                class="btn-link"
                @click="$router.push({ name: 'Login', query: { step: 'forgotPassword' } })"
              >
                Reset Password
              </button>
            </div>
          </form>

          <!-- Step: Forgot Password -->
          <div v-if="step === 'forgotPassword'" class="space-y-5">
            <div v-if="isSandbox" class="p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded">
              Password reset is not available in the sandbox environment. Please contact your organization administrator or
              <a href="mailto:support@kaironhealth.com" class="underline">support@kaironhealth.com</a>.
            </div>
            <form
              v-else
              class="space-y-5"
              @submit.prevent="submitForgotPassword"
            >
              <div>
                <label for="forgot-email" class="block text-sm font-medium text-gray-700 mb-1"
                  >Email</label
                >
                <input
                  id="forgot-email"
                  :value="email"
                  type="email"
                  disabled
                  class="p-2.5 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-500"
                />
              </div>
              <button
                ref="resetButton"
                type="submit"
                class="w-full btn-primary"
                :disabled="loading"
              >
                <span v-if="loading">
                  <i class="fa-solid fa-spinner fa-spin mr-2"></i>Sending...
                </span>
                <span v-else>Send Reset Link</span>
              </button>
            </form>
            <div>
              <button
                type="button"
                class="btn-link"
                @click="$router.push({ name: 'Login', query: { step: 'password' } })"
              >
                <i class="fa-solid fa-arrow-left mr-1"></i>Back to login
              </button>
            </div>
          </div>
        </div>
        <p class="mt-4 text-center text-xs text-gray-400">
          Having trouble?
          <a href="mailto:support@kaironhealth.com" class="text-neutral-600 hover:text-neutral-800 underline">Contact support</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { emailLookup } from '@/services/api/auth';
import { makeAllauthRequestAndParseErrors } from '@/utils/auth';
import koMark from '@/assets/kairon_logo_orange.svg';

export default {
  data() {
    return {
      email: '',
      password: '',
      loading: false,
      error: '',
      success: '',
      sessionExpired: false,
      koMark,
    };
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated']),
    step() {
      const s = this.$route.query.step;
      if (s === 'password' || s === 'forgotPassword') return s;
      return 'email';
    },
    isSandbox() {
      return import.meta.env.VITE_ENV === 'sandbox';
    },
  },
  watch: {
    step(newStep) {
      this.error = '';
      this.success = '';
      if (newStep === 'email') {
        this.password = '';
      }
      this.focusStep(newStep);
    },
  },
  mounted() {
    if (this.$route.query.sessionExpired) {
      this.sessionExpired = true;
    }
    if (this.$route.query.ssoError) {
      this.error = 'Microsoft login failed. Please try again.';
    }
    if (this.$route.query.adminConsent === 'granted') {
      this.success =
        'Admin consent granted successfully. Your organization can now use SSO to sign in.';
    } else if (this.$route.query.adminConsent === 'unknownTenant') {
      const tenantId = this.$route.query.microsoftTenantId || 'unknown';
      this.success =
        `Admin consent granted, but your organization is not yet configured in Kairon. Please share your Microsoft tenant ID with Kairon support: ${tenantId}`;
    } else if (this.$route.query.adminConsent === 'missingTenantId') {
      this.error =
        'Admin consent redirect was missing the tenant ID. Please try again or contact support.';
    }
    if (this.isAuthenticated) {
      this.$router.push({ name: 'Home' });
    }
    const s = this.$route.query.step;
    if ((s === 'password' || s === 'forgotPassword') && !this.email) {
      this.$router.replace({ name: 'Login' });
    }
    this.focusStep(this.step);
  },
  methods: {
    ...mapActions('auth', ['login', 'fetchUser']),
    ...mapActions('entity', ['initializeEntities']),
    async submitEmail() {
      this.error = '';
      if (!this.email || !this.email.includes('@')) {
        this.error = 'Please enter a valid email address.';
        return;
      }
      this.loading = true;
      try {
        const result = await emailLookup(this.email);
        if (result.method === 'sso' && result.redirect_url) {
          window.location.href = result.redirect_url;
          return;
        }
        this.$router.push({ name: 'Login', query: { step: 'password' } });
      } catch {
        this.error = 'Something went wrong. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    async submitLogin() {
      this.error = '';
      this.loading = true;
      try {
        await this.login({ email: this.email, password: this.password });
        await this.commonLogin();
      } catch (error) {
        const fields = error.fieldBasedErrors;
        const msg =
          fields?.non_field_errors?.[0] || fields?.password?.[0] || fields?.email?.[0];
        this.error = msg || 'Login failed. Please check your credentials.';
      } finally {
        this.loading = false;
      }
    },
    async submitForgotPassword() {
      this.error = '';
      this.loading = true;
      try {
        const lookup = await emailLookup(this.email);
        if (lookup.method === 'sso') {
          this.error = 'This account uses Microsoft SSO. Please sign in using your organization\'s SSO login instead.';
          return;
        }
        await makeAllauthRequestAndParseErrors({
          method: 'post',
          url: '_allauth/browser/v1/auth/password/request',
          data: { email: this.email },
        });
        this.success =
          'If an account exists for this email, a password reset link was sent. Please check your email.';
      } catch (error) {
        const fields = error.fieldBasedErrors;
        const msg = fields?.email?.[0] || fields?.non_field_errors?.[0];
        this.error = msg || 'Something went wrong. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    async commonLogin() {
      const lastVisitedRoute = localStorage.getItem('last_visited_route');
      await this.fetchUser();
      if (this.$store.getters['auth/passwordExpired']) {
        this.$router.push({ name: 'ChangePassword' });
        return;
      }
      await this.initializeEntities();
      this.$router.push(lastVisitedRoute || '/');
    },
    goBack() {
      this.$router.push({ name: 'Login' });
    },
    focusStep(step) {
      this.$nextTick(() => {
        if (step === 'email') this.$refs.emailInput?.focus();
        else if (step === 'password') this.$refs.passwordInput?.focus();
        else if (step === 'forgotPassword') this.$refs.resetButton?.focus();
      });
    },
  },
};
</script>
